import { useState, useRef, useEffect } from 'react'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline'
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline'
import IconButton from '@material-ui/core/IconButton'
import { withStyles } from '@material-ui/core/styles'
import { makeStyles } from '@material-ui/core/styles'

import ImageManager from '~/products/containers/image-manager'
import { useAppSelector } from '~/store'
import { get, post } from '~/tools/request'

const useStyles = makeStyles((theme) => ({
    agentSettings: {},
    settingField: {
        display: 'flex',
        marginBottom: 10,
    },
    settingLabel: {
        width: '50%',
        paddingRight: 20,
        paddingTop: 12,
        textAlign: 'right',
    },
    settingValue: {
        width: '50%',
        textAlign: 'left',
    },
    formControl: {
        textAlign: 'left',
    },
}))

const VoicePlayer = ({ voice }) => {
    const [isPlaying, setIsPlaying] = useState(false)
    const audioRef = useRef(null)

    const staticFilesDomain = useAppSelector((state) => state.initial.staticFilesDomain)
    const audioUrl = `${staticFilesDomain}voices/${voice}_en.m4a`

    // Reset playing state when voice changes
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
        }
        setIsPlaying(false)
    }, [voice])

    const handlePlayStop = () => {
        if (isPlaying) {
            // Stop the audio
            audioRef.current.pause()
            audioRef.current.currentTime = 0
            setIsPlaying(false)
        } else {
            // Play the audio
            audioRef.current.play()
            setIsPlaying(true)
        }
    }

    const handleAudioEnd = () => {
        setIsPlaying(false)
    }

    const handleAudioError = (e) => {
        console.error('Audio failed to load:', e)
        setIsPlaying(false)
    }

    return (
        <>
            <IconButton onClick={handlePlayStop}>
                {isPlaying ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
            </IconButton>

            <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={handleAudioEnd}
                onError={handleAudioError}
                preload="metadata"
            />
        </>
    )
}

const languages = [
    {
        name: 'English',
        value: 'en',
    },
    {
        name: 'Thai',
        value: 'th',
    },
]

const logoUploaderStyles = (theme) => ({
    imageManager: {
        marginTop: 0,
        flexDirection: 'column',
        width: 250,
    },
    dropzone: {
        order: 2,
        marginTop: 10,
        height: 50,
        width: '100%',
    },
    preview: {
        paddingLeft: 0,
        marginLeft: 0,
        order: 1,
        width: '100%',
        overflowX: 'auto',
    },
})

const LogoImageUploader = withStyles(logoUploaderStyles)(ImageManager)

export default function AgentSettings({ agentInfo, onSubmitted }) {
    const classes = useStyles()

    const [voice, setVoice] = useState('')
    const [voices, setVoices] = useState([])
    const [mainLanguage, setMainLanguage] = useState(languages[0].value)
    const [storeInfo, setStoreInfo] = useState('')
    const [storeId, setStoreId] = useState(null)
    const [logoFile, setLogoFile] = useState(null)

    const { bulkUpdateSettingValueAPIURL, agentOnboardingAPIURL } = useAppSelector(
        (state) => state.initial.endpoints
    )

    const fetchAgentOnboardingInfo = async () => {
        const { ok, ...data } = await get(agentOnboardingAPIURL)
        if (ok) {
            setAgentInfo(data)
        }
    }

    const setAgentInfo = (data) => {
        setVoices(data.voices)
        setVoice(data.settings['agent.voice'])
        setMainLanguage(data.settings['agent.main_language'])
        setStoreInfo(data.settings['agent.store_info'])
        setStoreId(data.store.id)
        setLogoFile(data.settings['store.logo'] ? { image: data.settings['store.logo'] } : null)
    }

    const handleLogoChange = (data, callback) => {
        setLogoFile(data?.length ? data[data.length - 1] : null)
        callback?.()
    }

    const handleSaveSettings = async () => {
        const data = {
            settings: {
                'agent.voice': voice,
                'agent.store_info': storeInfo,
                'agent.main_language': mainLanguage,
                'store.logo': logoFile ? logoFile.image : null,
            },
        }
        const res = await post(
            bulkUpdateSettingValueAPIURL
                .replace('source_type', 'store')
                .replace('source_id', storeId),
            data
        )
        if (res.ok) {
            onSubmitted && onSubmitted()
        }
    }

    useEffect(() => {
        if (agentInfo) {
            setAgentInfo(agentInfo)
        } else {
            fetchAgentOnboardingInfo()
        }
    }, [])

    return (
        <div className={classes.agentSettings}>
            <div className={classes.settingField}>
                <Typography className={classes.settingLabel} variant="body1">
                    {gettext('Select agent voice')}
                </Typography>
                <div className={classes.settingValue}>
                    <FormControl className={classes.formControl}>
                        <InputLabel id="voice-label">{gettext('Voice')}</InputLabel>
                        <Select
                            labelId="voice-label"
                            id="voice-select"
                            value={voice}
                            style={{ width: 150 }}
                            onChange={(e) => setVoice(e.target.value)}
                        >
                            {voices.map((item) => (
                                <MenuItem value={item.value}>{item.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <VoicePlayer voice={voice} />
                </div>
            </div>
            <div className={classes.settingField} style={{ display: 'none' }}>
                <Typography className={classes.settingLabel} variant="body1">
                    {gettext('Agent main language')}
                </Typography>
                <div className={classes.settingValue}>
                    <FormControl className={classes.formControl}>
                        <InputLabel id="language-label">{gettext('Main language')}</InputLabel>
                        <Select
                            labelId="language-label"
                            id="language-select"
                            style={{ width: 150 }}
                            value={mainLanguage}
                            onChange={(e) => setMainLanguage(e.target.value)}
                        >
                            {languages.map((item) => (
                                <MenuItem value={item.value}>{item.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </div>
            <div className={classes.settingField}>
                <Typography className={classes.settingLabel} variant="body1">
                    {gettext('Store logo')}
                </Typography>
                <div className={classes.settingValue}>
                    <LogoImageUploader
                        storeID={storeId}
                        images={logoFile ? [{ ...logoFile }] : []}
                        uploadText={gettext('Drop logo here or click to select')}
                        imagesChangedHandler={handleLogoChange}
                    />
                </div>
            </div>
            <div className={classes.settingField}>
                <Typography className={classes.settingLabel} variant="body1">
                    {gettext('Add information about the store')}
                </Typography>
                <div className={classes.settingValue}>
                    <FormControl className={classes.formControl}>
                        <TextField
                            id="store-info"
                            label="Store notes"
                            placeholder="Return policy, FAQ, Brand store"
                            multiline
                            style={{ width: 300 }}
                            value={storeInfo}
                            onChange={(e) => setStoreInfo(e.target.value)}
                        />
                    </FormControl>
                </div>
            </div>
            <div className={classes.settingField}>
                <Typography className={classes.settingLabel} variant="body1"></Typography>
                <div className={classes.settingValue}>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.submitButton}
                        onClick={handleSaveSettings}
                    >
                        {gettext('Save settings')}
                    </Button>
                </div>
            </div>
        </div>
    )
}
